import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import CredoraConfirmDialog from "./CredoraConfirmDialog";
import CredoraPromptDialog from "./CredoraPromptDialog";
import credoraToast from "./credoraToast";

const ConfirmDialogContext = createContext(null);

const INITIAL_STATE = {
  isOpen: false,
  title: "",
  message: "",
  confirmLabel: "Confirm",
  cancelLabel: "Cancel",
  tone: "primary",
  loading: false,
  error: "",
};

const PROMPT_INITIAL_STATE = {
  isOpen: false,
  title: "",
  message: "",
  label: "",
  placeholder: "",
  confirmLabel: "Submit",
  cancelLabel: "Cancel",
  tone: "primary",
  loading: false,
  error: "",
  inputValue: "",
  required: false,
  multiline: false,
  rows: 3,
};

/**
 * @typedef {Object} ConfirmDialogOptions
 * @property {string} title
 * @property {string} [message]
 * @property {string} [confirmLabel]
 * @property {string} [cancelLabel]
 * @property {"primary"|"danger"|"warning"|"success"} [tone]
 * @property {() => void | Promise<void>} [onConfirm]
 * @property {string} [successMessage]
 */

/**
 * @typedef {Object} PromptDialogOptions
 * @property {string} title
 * @property {string} label
 * @property {string} [message]
 * @property {string} [placeholder]
 * @property {string} [defaultValue]
 * @property {string} [confirmLabel]
 * @property {string} [cancelLabel]
 * @property {"primary"|"danger"|"warning"|"success"} [tone]
 * @property {boolean} [required]
 * @property {boolean} [multiline]
 * @property {number} [rows]
 * @property {(value: string) => void | Promise<void>} [onConfirm]
 * @property {string} [successMessage]
 */

export function ConfirmDialogProvider({ children }) {
  const [state, setState] = useState(INITIAL_STATE);
  const [promptState, setPromptState] = useState(PROMPT_INITIAL_STATE);
  const resolveRef = useRef(null);
  const onConfirmRef = useRef(null);
  const successMessageRef = useRef(null);
  const promptResolveRef = useRef(null);
  const promptOnConfirmRef = useRef(null);
  const promptSuccessMessageRef = useRef(null);

  const close = useCallback((result) => {
    setState(INITIAL_STATE);
    const resolve = resolveRef.current;
    resolveRef.current = null;
    onConfirmRef.current = null;
    successMessageRef.current = null;
    resolve?.(result);
  }, []);

  const closePrompt = useCallback((result) => {
    setPromptState(PROMPT_INITIAL_STATE);
    const resolve = promptResolveRef.current;
    promptResolveRef.current = null;
    promptOnConfirmRef.current = null;
    promptSuccessMessageRef.current = null;
    resolve?.(result);
  }, []);

  const confirm = useCallback(
    /** @param {ConfirmDialogOptions} options */
    (options) =>
      new Promise((resolve) => {
        resolveRef.current = resolve;
        onConfirmRef.current = options.onConfirm ?? null;
        successMessageRef.current = options.successMessage ?? null;
        setState({
          ...INITIAL_STATE,
          isOpen: true,
          title: options.title,
          message: options.message ?? "",
          confirmLabel: options.confirmLabel ?? "Confirm",
          cancelLabel: options.cancelLabel ?? "Cancel",
          tone: options.tone ?? "primary",
        });
      }),
    []
  );

  const prompt = useCallback(
    /** @param {PromptDialogOptions} options */
    (options) =>
      new Promise((resolve) => {
        promptResolveRef.current = resolve;
        promptOnConfirmRef.current = options.onConfirm ?? null;
        promptSuccessMessageRef.current = options.successMessage ?? null;
        setPromptState({
          ...PROMPT_INITIAL_STATE,
          isOpen: true,
          title: options.title,
          message: options.message ?? "",
          label: options.label,
          placeholder: options.placeholder ?? "",
          confirmLabel: options.confirmLabel ?? "Submit",
          cancelLabel: options.cancelLabel ?? "Cancel",
          tone: options.tone ?? "primary",
          inputValue: options.defaultValue ?? "",
          required: options.required ?? false,
          multiline: options.multiline ?? false,
          rows: options.rows ?? 3,
        });
      }),
    []
  );

  const handleConfirm = useCallback(async () => {
    const action = onConfirmRef.current;
    if (!action) {
      close(true);
      return;
    }
    setState((prev) => ({ ...prev, loading: true, error: "" }));
    try {
      await action();
      if (successMessageRef.current) {
        credoraToast.success(successMessageRef.current);
      }
      close(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const message =
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
            ? detail.map((d) => d.msg || JSON.stringify(d)).join("; ")
            : err?.message || "Something went wrong.";
      setState((prev) => ({ ...prev, loading: false, error: message }));
    }
  }, [close]);

  const handlePromptConfirm = useCallback(async () => {
    const value = promptState.inputValue.trim();
    if (promptState.required && !value) {
      setPromptState((prev) => ({ ...prev, error: `Please enter ${prev.label.toLowerCase()}.` }));
      return;
    }

    const action = promptOnConfirmRef.current;
    if (!action) {
      closePrompt(value || null);
      return;
    }

    setPromptState((prev) => ({ ...prev, loading: true, error: "" }));
    try {
      await action(value);
      if (promptSuccessMessageRef.current) {
        credoraToast.success(promptSuccessMessageRef.current);
      }
      closePrompt(value);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const message =
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
            ? detail.map((d) => d.msg || JSON.stringify(d)).join("; ")
            : err?.message || "Something went wrong.";
      setPromptState((prev) => ({ ...prev, loading: false, error: message }));
    }
  }, [closePrompt, promptState.inputValue, promptState.required]);

  const handleCancel = useCallback(() => {
    if (state.loading) {
      return;
    }
    close(false);
  }, [close, state.loading]);

  const handlePromptCancel = useCallback(() => {
    if (promptState.loading) {
      return;
    }
    closePrompt(null);
  }, [closePrompt, promptState.loading]);

  const handlePromptInputChange = useCallback((value) => {
    setPromptState((prev) => ({ ...prev, inputValue: value, error: "" }));
  }, []);

  const value = useMemo(() => ({ confirm, prompt }), [confirm, prompt]);

  return (
    <ConfirmDialogContext.Provider value={value}>
      {children}
      <CredoraConfirmDialog
        isOpen={state.isOpen}
        toggle={handleCancel}
        title={state.title}
        message={state.message}
        confirmLabel={state.confirmLabel}
        cancelLabel={state.cancelLabel}
        tone={state.tone}
        loading={state.loading}
        error={state.error}
        onConfirm={handleConfirm}
      />
      <CredoraPromptDialog
        isOpen={promptState.isOpen}
        toggle={handlePromptCancel}
        title={promptState.title}
        message={promptState.message}
        label={promptState.label}
        placeholder={promptState.placeholder}
        inputValue={promptState.inputValue}
        onInputChange={handlePromptInputChange}
        confirmLabel={promptState.confirmLabel}
        cancelLabel={promptState.cancelLabel}
        tone={promptState.tone}
        loading={promptState.loading}
        error={promptState.error}
        required={promptState.required}
        multiline={promptState.multiline}
        rows={promptState.rows}
        onConfirm={handlePromptConfirm}
      />
    </ConfirmDialogContext.Provider>
  );
}

export function useConfirmDialog() {
  const ctx = useContext(ConfirmDialogContext);
  if (!ctx) {
    throw new Error("useConfirmDialog must be used within ConfirmDialogProvider");
  }
  return ctx;
}
